/**
 * Product Page Functionality
 * Extracted from sections/product.liquid for better caching
 * Handles gallery, variants, quantity controls, and add to cart
 */

document.addEventListener('DOMContentLoaded', function() {
  try {
    initProductPage();
  } catch (error) {
    /*
     * A throw anywhere in init used to abort the REST of the handler silently: the browser
     * swallows an exception raised inside an event listener, so the page looked fine and the
     * damage surfaced somewhere unrelated and much later. #138 round 3 lost a gate round to
     * exactly that - one temporal-dead-zone ReferenceError near the top meant no variant
     * listeners, no inventory warning, no delivery estimate and no sticky-bar observer on any
     * available PDP, with an empty console.
     *
     * Everything below is progressive enhancement over markup Liquid has already rendered, so
     * catching is the right call: the page degrades to its server-rendered state instead of
     * half-initialising. But it must never be quiet again.
     */
    console.error('[product-page] init failed', error);
  }
});

/**
 * All product-page behaviour. Runs once, synchronously, on DOMContentLoaded.
 *
 * ORDERING RULE - read this before adding an eager call. Every `const` below is in its
 * temporal dead zone until its own line executes, and the functions here are hoisted
 * declarations that close over those `const`s. So a function invoked ABOVE the declarations it
 * touches throws `ReferenceError: Cannot access 'x' before initialization`, even though the
 * function itself is perfectly reachable. Declarations first, invocations last: put new init
 * calls in the "Initialize on page load" block near the bottom, never inline next to the
 * function they call.
 */
function initProductPage() {
  // Product Gallery Functionality
  const galleryThumbs = document.querySelectorAll('[data-product-gallery-thumb]');
  const galleryMain = document.querySelector('[data-product-gallery-main]');
  const featuredImage = galleryMain?.querySelector('.product-gallery__featured-image img');

  // Preload all product images for smooth switching
  const imagePreloader = [];
  const productImages = window.NATURAH_PRODUCT_IMAGES || [];

  productImages.forEach(imageSrc => {
    const img = new Image();
    img.src = imageSrc;
    imagePreloader.push(img);
  });

  // Add product-card-style effects to main image wrapper
  if (featuredImage) {
    const imageWrapper = featuredImage.parentElement;

    // Base styling with brand border and gradient - remove any default borders
    imageWrapper.style.borderRadius = '12px';
    imageWrapper.style.overflow = 'hidden';
    imageWrapper.style.position = 'relative';
    imageWrapper.style.border = '2px solid var(--brand-700)';
    imageWrapper.style.outline = 'none';
    imageWrapper.style.background = 'linear-gradient(135deg, rgba(180, 212, 85, 0.08), rgba(201, 224, 133, 0.05))';
    imageWrapper.style.backgroundClip = 'padding-box';
    imageWrapper.style.transition = 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.3s ease, border-color 0.3s ease';
    imageWrapper.style.cursor = 'pointer';

    // Ensure the featured image itself has no borders
    if (featuredImage) {
      featuredImage.style.border = 'none';
      featuredImage.style.outline = 'none';
      featuredImage.style.margin = '0';
      featuredImage.style.padding = '0';
    }

    // Add subtle outer gradient border effect
    imageWrapper.style.setProperty('--gradient-border', 'linear-gradient(135deg, var(--brand-700), var(--brand-600), var(--brand-700))');
    imageWrapper.style.setProperty('--base-shadow', '0 2px 8px rgba(180, 212, 85, 0.15)');
    imageWrapper.style.boxShadow = 'var(--base-shadow), 0 0 0 1px rgba(180, 212, 85, 0.1)';

    // Simple gradient border effect without hover + border cleanup
    const gradientBorderStyle = `
      .product-gallery__featured {
        /* Ensure no extra borders from anywhere */
        box-sizing: border-box !important;
      }

      .product-gallery__featured *,
      .product-gallery__featured *::before,
      .product-gallery__featured *::after {
        border: none !important;
        outline: none !important;
      }

      .product-gallery__featured {
        border: 2px solid var(--brand-700) !important;
      }

      .product-gallery__featured::before {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        background: linear-gradient(135deg, var(--brand-700), var(--brand-600), var(--brand-700));
        border-radius: 14px;
        z-index: -1;
        opacity: 0.3;
      }
    `;

    if (!document.querySelector('#gradient-border-style')) {
      const style = document.createElement('style');
      style.id = 'gradient-border-style';
      style.textContent = gradientBorderStyle;
      document.head.appendChild(style);
    }

    // Add gentle breathing animation to border
    if (!document.querySelector('#gallery-animations')) {
      const style = document.createElement('style');
      style.id = 'gallery-animations';
      style.textContent = `
        .product-gallery__featured {
          animation: borderGlow 6s ease-in-out infinite;
        }

        @keyframes borderGlow {
          0%, 100% {
            border-color: var(--brand-700);
          }
          50% {
            border-color: var(--brand-600);
          }
        }
      `;
      document.head.appendChild(style);
    }
  }

  // Refined image changing function with subtle transitions
  window.changeMainImage = function(newImageSrc, clickedThumb) {
    if (featuredImage) {
      const imageWrapper = featuredImage.parentElement;

      // Immediate, gentle transitions with early start easing
      featuredImage.style.transition = 'opacity 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      imageWrapper.style.transition = 'border-color 0.3s ease, box-shadow 0.3s ease';

      // Very subtle effects - less is more
      imageWrapper.style.borderColor = 'rgba(180, 212, 85, 0.4)';
      imageWrapper.style.boxShadow = '0 4px 20px rgba(180, 212, 85, 0.12)';

      // Gentle fade with minimal scale
      featuredImage.style.opacity = '0.6';
      featuredImage.style.transform = 'scale(0.995)';

      // Create new image to preload
      const newImg = new Image();
      newImg.onload = function() {
        // Quick, smooth timing
        setTimeout(() => {
          // Switch the image
          featuredImage.src = newImageSrc;
          featuredImage.removeAttribute('srcset');

          // Restore with gentle motion
          featuredImage.style.opacity = '1';
          featuredImage.style.transform = 'scale(1)';

          // Return to subtle animated state
          setTimeout(() => {
            imageWrapper.style.borderColor = 'rgba(180, 212, 85, 0.15)';
            imageWrapper.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
          }, 300);

        }, 120); // Faster, more responsive timing
      };
      newImg.onerror = function() {
        // Graceful fallback
        setTimeout(() => {
          featuredImage.style.opacity = '1';
          featuredImage.style.transform = 'scale(1)';
          imageWrapper.style.borderColor = 'rgba(180, 212, 85, 0.15)';
          imageWrapper.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
        }, 120);
      };
      newImg.src = newImageSrc;
    }

    // Update active states immediately
    const allThumbs = document.querySelectorAll('[data-product-gallery-thumb]');
    allThumbs.forEach(thumb => {
      thumb.style.border = '2px solid transparent';
      thumb.classList.remove('product-gallery__thumbnail--active');
    });

    clickedThumb.style.border = '2px solid #b4d455';
    clickedThumb.classList.add('product-gallery__thumbnail--active');
  };

  // Quantity Controls
  const quantityInput = document.querySelector('[data-quantity-input]');
  const quantityDecrease = document.querySelector('[data-quantity-decrease]');
  const quantityIncrease = document.querySelector('[data-quantity-increase]');

  // Dynamic price update function
  function updatePriceDisplay() {
    const totalPriceElement = document.querySelector('[data-total-price]');
    const unitPriceElement = document.querySelector('[data-unit-price]');
    const quantityInput = document.querySelector('.quantity-input__input');

    if (quantityInput) {
      const quantity = parseInt(quantityInput.value) || 1;
      const currentVariant = getCurrentVariant();

      if (currentVariant) {
        // The Total row quotes what the shopper will actually be charged, so it multiplies
        // the DISCOUNTED unit price - the same figure the price row above it shows. Using
        // `variant.price` here made "Total" the one surface on the page still quoting the
        // pre-discount number (validator round 2).
        const unitPrice = pricePair(currentVariant).display;
        const totalPrice = unitPrice * quantity;

        // Update total price display
        if (totalPriceElement) {
          totalPriceElement.textContent = formatMoney(totalPrice);
        }

        // Show/hide unit price vs total when quantity > 1
        if (unitPriceElement && quantity > 1) {
          unitPriceElement.style.opacity = '0.7';
        } else if (unitPriceElement) {
          unitPriceElement.style.opacity = '1';
        }
      }
    }
  }

  // EXACT cart quantity handlers - copy from cart.liquid
  const plusButtons = document.querySelectorAll('.quantity-btn--plus');
  const minusButtons = document.querySelectorAll('.quantity-btn--minus');

  plusButtons.forEach(button => {
    button.addEventListener('click', function() {
      const input = this.parentNode.querySelector('.quantity-input__input');
      const currentValue = parseInt(input.value);
      input.value = currentValue + 1;

      // Update price display (instead of form submit)
      updatePriceDisplay();

      // Positive feedback for increasing quantity
      this.classList.add('quantity-btn--active');
      setTimeout(() => {
        this.classList.remove('quantity-btn--active');
      }, 200);
    });
  });

  minusButtons.forEach(button => {
    button.addEventListener('click', function() {
      const input = this.parentNode.querySelector('.quantity-input__input');
      const currentValue = parseInt(input.value);

      if (currentValue > 1) {
        input.value = currentValue - 1;
        updatePriceDisplay();
      }
    });
  });

  // Auto-update when quantity input changes directly
  const quantityInputs = document.querySelectorAll('.quantity-input__input');
  quantityInputs.forEach(input => {
    input.addEventListener('change', function() {
      updatePriceDisplay();
    });
  });

  // Product Options & Variant Selection
  const productForm = document.querySelector('[data-product-form]');
  const variantIdInput = document.querySelector('[data-product-variant-id]');
  const addToCartText = document.querySelector('[data-add-to-cart-text]');
  const addToCartButton = document.querySelector('[data-add-to-cart]');
  const productPricing = document.querySelector('[data-product-pricing]');

  /* #138 - the in-stock + delivery widget under the price. Absent on a sold-out PDP
     (#134 renders the back-in-stock capture instead), so every handler below no-ops. */
  const deliveryWidget = document.querySelector('[data-delivery]');
  const deliveryStock = deliveryWidget?.querySelector('[data-delivery-stock]');
  const deliveryArrival = deliveryWidget?.querySelector('[data-delivery-arrival]');
  const deliveryArrivalText = deliveryWidget?.querySelector('[data-delivery-arrival-text]');
  const addToCartCue = document.querySelector('[data-add-to-cart-cue]');

  /* Schema defaults for config/settings_schema.json > "Delivery & availability", restated
     as the LAST-RESORT fallback. The knobs ride on the widget as `data-ship-days-max` /
     `data-transit-days-max`; if an attribute is missing or unparseable the estimate falls
     back to these rather than bailing, because a widget that silently never appears is a
     worse answer than a date computed from the documented defaults. */
  const SHIP_DAYS_MAX_DEFAULT = 2;
  const TRANSIT_DAYS_MAX_DEFAULT = 5;

  /** Sanity ceiling on a hand-typed knob, so a stray 99999 cannot spin `addBusinessDays`. */
  const MAX_BUSINESS_DAYS = 365;

  // Variant data (populated by window.NATURAH_PRODUCT_DATA from Liquid template)
  const productData = window.NATURAH_PRODUCT_DATA || { variants: [], options: [] };

  function updateVariantSelection() {
    if (!productForm || !variantIdInput) return;

    const formData = new FormData(productForm);
    const selectedOptions = {};

    // Get selected options
    productData.options.forEach((option, index) => {
      const optionValue = formData.get(`options[${option}]`);
      if (optionValue) {
        selectedOptions[option] = optionValue;
      }
    });

    // Find matching variant
    const selectedVariant = productData.variants.find(variant => {
      return productData.options.every(option => {
        const variantOptionValue = variant.options[productData.options.indexOf(option)];
        return selectedOptions[option] === variantOptionValue;
      });
    });

    if (selectedVariant) {
      // Update variant ID
      variantIdInput.value = selectedVariant.id;

      // Update pricing
      updatePricing(selectedVariant);

      // ...and the Total row, which `updatePricing` does NOT touch. It lives in
      // `updatePriceDisplay` because it also depends on the quantity, so before this call it
      // was written only by the three quantity handlers and the one init call - meaning it
      // kept the PREVIOUS variant's price after every option click. Must stay below
      // `variantIdInput.value = ...` above: `updatePriceDisplay` re-reads the selection
      // through `getCurrentVariant()` rather than taking the variant as an argument.
      updatePriceDisplay();

      // Update the in-stock + delivery widget (#138)
      updateDeliveryWidget(selectedVariant);

      // Update add to cart button
      updateAddToCartButton(selectedVariant);

      // Update mobile sticky add-to-cart bar (#57) so it mirrors the selected variant
      updateStickyBar(selectedVariant);

      // Update inventory warning (NEW)
      updateInventoryWarning(selectedVariant);

      // Update main image if variant has featured image
      if (selectedVariant.featured_image && featuredImage) {
        featuredImage.src = selectedVariant.featured_image.src;
      }
    }
  }

  /**
   * #138 - the quiet "Only N remaining" line, kept in step with the selected variant.
   *
   * These two hooks were DEAD until this issue: no PDP markup carried
   * [data-inventory-warning], so the whole function returned on its first line. It now
   * mirrors the Liquid gate in sections/product.liquid one-for-one - available AND tracked
   * AND a positive quantity inside a positive threshold.
   *
   * `tracked` is inventory_management (null when Shopify is not counting this variant);
   * inferring that from `quantity === 0` would be a guess. The threshold is read with `??`
   * rather than `||` because 0 is the operator MEANING "switch the line off", and `||`
   * would silently rewrite that to the default.
   */
  function updateInventoryWarning(variant) {
    const inventoryWarning = document.querySelector('[data-inventory-warning]');
    const inventoryCount = inventoryWarning?.querySelector('[data-inventory-count]');

    if (!inventoryWarning || !inventoryCount) return;

    const inventoryData = window.NATURAH_VARIANT_INVENTORY?.[variant.id];
    const threshold = window.NATURAH_LOW_STOCK_THRESHOLD ?? 10;

    const low =
      !!inventoryData &&
      inventoryData.available &&
      !!inventoryData.tracked &&
      threshold > 0 &&
      inventoryData.quantity > 0 &&
      inventoryData.quantity <= threshold;

    inventoryCount.textContent = low ? inventoryData.quantity : '';
    inventoryWarning.hidden = !low;
  }

  /**
   * THE price derivation - one function, mirroring the Liquid block at the top of
   * sections/product.liquid line-for-line (validator round 1).
   *
   * ALL FIVE price surfaces on the PDP call this, so none of them can disagree about what a
   * variant costs:
   *   1. the price row            - `updatePricing`  -> `.product-pricing__sale` / `__regular`
   *   2. the percentage chip      - `updatePricing`  -> `.product-pricing__badge`
   *   3. the exact-saving chip    - `updatePricing`  -> `.product-pricing__saving`
   *   4. the Total row            - `updatePriceDisplay` -> `[data-total-price]` (x quantity)
   *   5. the CTA label + its two post-add relabels - `updateAddToCartButton` / the fetch
   *      callbacks -> `[data-add-to-cart-text]`
   *   6. the #57 sticky bar       - `updateStickyBar` -> `[data-sticky-atc-price]`
   * (Six entries, five surfaces - the CTA label and its relabels are the same element.)
   *
   * Every one of them used to read the raw `variant.price` or know only about
   * `compare_at_price`, so under a sitewide promo each quoted the UNDISCOUNTED figure over
   * the discounted one Liquid had seeded. Found in two passes: the price row, the chips and
   * the sticky bar in validator round 1, the Total row and the CTA label in round 2.
   *
   * Precedence matches Liquid exactly: `compare_at_price` wins, then the sitewide promo,
   * then no sale. The promo knobs come off `[data-product-pricing]` rather than being
   * written into the script - Standing Rule #3, no hardcoded economics.
   *
   * Liquid uses decimal arithmetic. Multiply integer cents by the retained whole percent
   * BEFORE dividing by 100, then round half cents up. For 165 cents at 30%, Liquid yields
   * 115.5 -> 116 cents. Dividing first in JS produces a binary 0.7 approximation and then
   * 115.49999999999999 -> 115, so matching Liquid's written order does not match its result.
   */
  function pricePair(variant) {
    const price = Number(variant?.price) || 0;
    const compareAt = Number(variant?.compare_at_price) || 0;

    if (compareAt > price) {
      return {
        original: compareAt,
        display: price,
        percent: Math.round(((compareAt - price) * 100) / compareAt),
        onSale: true
      };
    }

    // `data-promo-on` already folds in `settings.sitewide_promo_enabled` AND a non-zero
    // percentage, so this is the same single condition the Liquid `elsif` tests.
    const promoOn = productPricing?.dataset.promoOn === 'true';
    const percent = parseInt(productPricing?.dataset.discountPercent ?? '', 10);

    if (promoOn && Number.isInteger(percent) && percent > 0 && percent < 100) {
      return {
        original: price,
        display: Math.round((price * (100 - percent)) / 100),
        percent,
        onSale: true
      };
    }

    return { original: price, display: price, percent: 0, onSale: false };
  }

  /**
   * Keep the price row and both Save chips in step with the selected variant.
   *
   * Liquid always emits both unit prices, the compare price, and the two chips. Toggle
   * `hidden` so regular-to-sale and sale-to-regular transitions work from either initial
   * variant, including when the sitewide promotion is disabled.
   */
  function updatePricing(variant) {
    if (!productPricing) return;

    const regularPrice = productPricing.querySelector('.product-pricing__regular');
    const salePrice = productPricing.querySelector('.product-pricing__sale');
    const comparePrice = productPricing.querySelector('.product-pricing__compare');
    const percentBadge = productPricing.querySelector('.product-pricing__badge');
    const savingBadge = productPricing.querySelector('.product-pricing__saving');

    const { original, display, percent, onSale } = pricePair(variant);
    const saving = original - display;

    if (salePrice) {
      salePrice.hidden = !onSale;
      salePrice.textContent = formatMoney(display);
    }
    if (regularPrice) {
      regularPrice.hidden = onSale;
      regularPrice.textContent = formatMoney(display);
    }
    if (comparePrice) {
      const showCompare = onSale && saving > 0;
      comparePrice.hidden = !showCompare;
      if (showCompare) comparePrice.textContent = formatMoney(original);
    }

    // Both chips are derived from the SAME pair the row above just rendered, so neither can
    // claim a saving the price row no longer supports. Each disappears when it has nothing
    // to say - the JS mirror of the Liquid `exact_saving > 0` gate.
    if (percentBadge) {
      percentBadge.hidden = !(onSale && percent > 0);
      if (!percentBadge.hidden) percentBadge.textContent = `Save ${percent}%`;
    }
    if (savingBadge) {
      savingBadge.hidden = !(onSale && saving > 0);
      if (!savingBadge.hidden) savingBadge.textContent = `Save ${formatMoney(saving)}`;
    }
  }

  /**
   * Add whole BUSINESS days to a date - Saturdays and Sundays are neither counted nor
   * landed on.
   */
  function addBusinessDays(from, days) {
    const date = new Date(from.getTime());
    let remaining = days;

    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      const weekday = date.getDay();
      if (weekday !== 0 && weekday !== 6) remaining -= 1;
    }

    return date;
  }

  /**
   * Read one business-day knob off the widget's own dataset.
   *
   * `parseInt` on a missing attribute yields NaN, and NaN is exactly the value that used to
   * make the estimate bail and leave the arrival span hidden forever. Anything that is not
   * a plausible whole number of days resolves to the schema default instead, so this
   * function ALWAYS returns a usable number.
   */
  function businessDaysAttr(name, fallback) {
    const parsed = parseInt(deliveryWidget?.dataset?.[name] ?? '', 10);
    return Number.isInteger(parsed) && parsed >= 0 && parsed <= MAX_BUSINESS_DAYS ? parsed : fallback;
  }

  /**
   * #138 - "Arrives by <Weekday, Mon D>".
   *
   * Computed in the browser, not in Liquid, because a Liquid-rendered date is baked into a
   * CDN-cached page: the Monday shopper would be promised the previous Friday's arrival.
   * The estimate is the CEILING (handling max + transit max) so it is a date fulfilment
   * can hold rather than an average it misses half the time, and the locale is pinned to
   * en-US so the shape does not drift with the visitor's device.
   */
  function arrivalLabel() {
    const days =
      businessDaysAttr('shipDaysMax', SHIP_DAYS_MAX_DEFAULT) +
      businessDaysAttr('transitDaysMax', TRANSIT_DAYS_MAX_DEFAULT);

    return 'Arrives by ' + addBusinessDays(new Date(), days).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * #138 - the in-stock + delivery widget, in ONE pass: availability, then compute, then
   * write, then unhide. Called on load and on every variant change.
   *
   * ROUND-2 FIX. This used to be two functions - `updateDeliveryWidget` set
   * `hidden = !available || !arrivalText.textContent` and a separate `renderArrivalEstimate()`
   * filled the text afterwards - so the reveal depended on (a) the two running in the right
   * order, (b) `getCurrentVariant()` returning a variant at all, and (c) a `window` global
   * assigned by a different inline <script>. Any one of those failing left the span `hidden`
   * with no fallback and no error. The knobs now ride on the element, the computation cannot
   * bail, and nothing but genuine unavailability hides the span.
   *
   * `variant` may be undefined (the inline data block failed, or a template that does not
   * emit it). In that case DO NOT guess - trust the availability Liquid already rendered.
   *
   * An out-of-stock VARIANT on an otherwise-available product keeps the widget but drops the
   * ship window and the date: there is no honest date to promise for something you cannot buy.
   * `updateStockStatus` (the old green pill / orange "Only N left" badge in the header row)
   * is gone; the count moved to `updateInventoryWarning`.
   */
  function updateDeliveryWidget(variant) {
    if (!deliveryWidget || !deliveryStock) return;

    const available = variant
      ? !!variant.available
      : !deliveryWidget.classList.contains('product__delivery--out');

    deliveryWidget.classList.toggle('product__delivery--out', !available);
    deliveryStock.textContent = available ? 'In stock' : 'Out of stock';

    if (!deliveryArrival || !deliveryArrivalText) return;

    // Compute and WRITE first, unhide second - never the other way round.
    deliveryArrivalText.textContent = available ? arrivalLabel() : '';
    deliveryArrival.hidden = !available;
  }

  function updateAddToCartButton(variant) {
    if (!addToCartButton || !addToCartText) return;

    if (variant.available) {
      addToCartButton.disabled = false;
      addToCartText.textContent = `Add to Cart - ${formatMoney(pricePair(variant).display)}`;
    } else {
      addToCartButton.disabled = true;
      addToCartText.textContent = 'Sold Out';
    }

    // #138 - the directional cue only makes sense pointing at a live action.
    if (addToCartCue) addToCartCue.hidden = !variant.available;
  }

  // One money formatter across the theme: delegate to window.NaturahCartUI.formatMoney
  // (assets/cart-drawer.js), which honours shop.money_format so the PDP matches the cart
  // page + drawer. cart-drawer.js loads (defer, body-top) before this file, so the shared
  // helper is defined by the time any handler here runs. The en-US/USD Intl path is a
  // last-resort fallback if the drawer script is ever absent on a template.
  function formatMoney(cents) {
    if (window.NaturahCartUI && typeof window.NaturahCartUI.formatMoney === 'function') {
      return window.NaturahCartUI.formatMoney(cents);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }

  // Listen for option changes
  const optionInputs = document.querySelectorAll('[data-option-value]');
  optionInputs.forEach(input => {
    input.addEventListener('change', updateVariantSelection);
  });

  /*
   * Initialize on page load. THE ONLY PLACE eager calls belong - every `const` and every
   * function above has been evaluated by the time this runs.
   *
   * `updatePriceDisplay()` used to be called ~270 lines earlier, right after the quantity
   * handlers it belongs to. It reaches `getCurrentVariant()`, which reads `variantIdInput`
   * and `productData` - both declared below that point - so on any PDP with a quantity input
   * (i.e. every purchasable product) it threw and killed the whole handler. Same tick either
   * way, so moving it down changes nothing a shopper can see.
   *
   * `updateDeliveryWidget` is called UNCONDITIONALLY: it handles an undefined variant by
   * trusting the server-rendered availability, so the arrival date still appears even if the
   * inline product-data block never produced a variant list.
   */
  updatePriceDisplay();

  const currentVariant = getCurrentVariant();
  if (currentVariant) {
    updateInventoryWarning(currentVariant);
  }
  updateDeliveryWidget(currentVariant);

  // Add to Cart Functionality
  if (productForm) {
    productForm.addEventListener('submit', function(e) {
      e.preventDefault();

      const formData = new FormData(this);
      const loadingElement = document.querySelector('[data-loading]');

      const addQuantity = parseInt(quantityInput?.value) || 1;

      // Show loading state. The #138 cue rides with the label: an arrow left hanging
      // beside a spinner points at nothing.
      if (addToCartText) addToCartText.style.display = 'none';
      if (addToCartCue) addToCartCue.hidden = true;
      if (loadingElement) loadingElement.style.display = 'block';
      if (addToCartButton) addToCartButton.disabled = true;

      // Optimistic: update badge instantly before network request
      // Uses optional chaining — safe if quick-add.js hasn't loaded yet
      window.NaturahCart?.optimisticAdd(addQuantity);

      // Add to cart via Shopify Ajax API
      fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
      .then(response => response.json())
      .then(data => {
        console.log('Added to cart:', data);

        // Reconcile with actual server count
        window.NaturahCart?.reconcile();

        // #57: notify the cart drawer (and any other listeners) so it opens on the
        // PDP's primary add path — matching the exact event contract quick-add.js uses
        // ({ variant: <cart/add.js response>, quantity }). The #54 drawer listens on this.
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { variant: data, quantity: addQuantity }
        }));

        // Show success state briefly
        if (addToCartText) {
          addToCartText.textContent = 'Added to Cart!';
          addToCartText.style.display = 'block';
        }

        setTimeout(() => {
          if (addToCartText) {
            addToCartText.textContent = `Add to Cart - ${formatMoney(pricePair(getCurrentVariant()).display)}`;
          }
        }, 2000);
      })
      .catch(error => {
        console.error('Error adding to cart:', error);

        // Revert optimistic update on failure
        window.NaturahCart?.revert(addQuantity);

        // Show error state
        if (addToCartText) {
          addToCartText.textContent = 'Error - Try Again';
          addToCartText.style.display = 'block';
        }

        setTimeout(() => {
          if (addToCartText) {
            addToCartText.textContent = `Add to Cart - ${formatMoney(pricePair(getCurrentVariant()).display)}`;
          }
        }, 2000);
      })
      .finally(() => {
        // Hide loading state
        if (loadingElement) loadingElement.style.display = 'none';
        if (addToCartText) addToCartText.style.display = 'block';
        if (addToCartCue) addToCartCue.hidden = !getCurrentVariant()?.available;
        if (addToCartButton) addToCartButton.disabled = false;
      });
    });
  }

  function getCurrentVariant() {
    const variantId = variantIdInput?.value;
    return productData.variants.find(v => v.id == variantId) || productData.variants[0];
  }

  /* ------------------------------------------------------------------ *
   * #57 — Mobile sticky add-to-cart bar
   * Fixed to the viewport bottom; revealed by an IntersectionObserver once the
   * main buy box scrolls out of view, hidden again when it returns. Its Add button
   * re-runs the main form's ajax add path (which now dispatches cart:item-added),
   * so the #54 drawer opens exactly as it does for the primary PDP add.
   * ------------------------------------------------------------------ */
  const stickyBar = document.querySelector('[data-sticky-atc]');
  const stickyAddBtn = stickyBar?.querySelector('[data-sticky-atc-add]');
  // #134 — on a fully sold-out PDP the Add is replaced by a "Notify me" anchor pointing at
  // the back-in-stock form. Only one of the two ever exists in the bar.
  const stickyNotifyLink = stickyBar?.querySelector('[data-sticky-atc-notify]');
  const stickyAddText = stickyBar?.querySelector('[data-sticky-atc-add-text]');
  const stickyPrice = stickyBar?.querySelector('[data-sticky-atc-price]');
  const stickyVariant = stickyBar?.querySelector('[data-sticky-atc-variant]');

  function updateStickyBar(variant) {
    if (!stickyBar || !variant) return;

    // Same derivation as every other price surface - see the `pricePair` docblock for the
    // full list. Writing `variant.price` here put the UNDISCOUNTED price in the sticky bar
    // over the `{{ display_price | money }}` Liquid had seeded, so the two disagreed the
    // moment a variant changed.
    if (stickyPrice) stickyPrice.textContent = formatMoney(pricePair(variant).display);

    if (stickyVariant) {
      const label = variant.title && variant.title !== 'Default Title' ? variant.title : '';
      stickyVariant.textContent = label;
      stickyVariant.hidden = label === '';
    }

    if (stickyAddBtn && stickyAddText) {
      stickyAddBtn.disabled = !variant.available;
      stickyAddText.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }
  }

  if (stickyBar) {
    // Sticky Add reuses the main buy box's add path (same variant, same quantity, same
    // cart:item-added dispatch → drawer opens). No duplicate fetch logic.
    stickyAddBtn?.addEventListener('click', function () {
      if (stickyAddBtn.disabled || !productForm) return;
      if (typeof productForm.requestSubmit === 'function') {
        productForm.requestSubmit();
      } else {
        productForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    });

    // #134 — the notify CTA is a real anchor (it works with JS off); JS only upgrades the
    // jump into a smooth scroll that centres the form and puts the caret in the email field.
    stickyNotifyLink?.addEventListener('click', function (e) {
      const target = document.getElementById('back-in-stock');
      if (!target) return; // no form on the page — let the bare anchor do its default thing

      e.preventDefault();
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      target.querySelector('input[type="email"]')?.focus({ preventScroll: true });
    });

    // Reveal the bar only while the buy box is out of view. position:fixed keeps the
    // bar out of flow, so toggling it never shifts layout (no CLS).
    const buyBox = document.querySelector('[data-buy-box]') || addToCartButton;
    if (buyBox && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          const show = !entry.isIntersecting;
          stickyBar.classList.toggle('is-visible', show);
          stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
        });
      }, { threshold: 0 });
      observer.observe(buyBox);
    }
  }
}
